import {
  Settings,
  ArrowDownToLine,
  ArrowUpFromLine,
  FileSpreadsheet,
  Users,
  Shield,
  Package,
  Megaphone,
  ShoppingCart,
  ClipboardList,
  FileText,
  Scissors,
  Sparkles,
  BellRing,
  Truck,
  Mail,
} from 'lucide-react';
import type { RoadmapMilestone } from '../types';

// Ordering: chronological top-to-bottom (oldest live → newest live → in-progress / planned).
export const ROADMAP_MILESTONES: RoadmapMilestone[] = [
  {
    id: 'system-administration',
    name: 'System Administration',
    tagline: 'The foundation — users, roles, modules, and announcements.',
    purpose:
      'Everything the platform needs before any operational work can happen. Who can log in, what they are allowed to do, which modules are turned on, and how the team hears about changes.',
    status: 'live',
    deployedAt: '2026-01-28',
    icon: Settings,
    highlights: [
      'Force-change-password flow on first login',
      'Granular per-module permissions',
      'Toggle modules into maintenance mode without a redeploy',
      'Centralised release announcement feed',
    ],
    submodules: [
      { code: 'USEM', name: 'User Management', status: 'live', icon: Users },
      { code: 'AUTM', name: 'Authorization', status: 'live', icon: Shield },
      { code: 'MODM', name: 'Module Management', status: 'live', icon: Package },
      { code: 'RELE', name: 'Release Notes', status: 'live', icon: Megaphone },
      { code: 'ANNT', name: 'Announcements', status: 'live', icon: BellRing },
    ],
  },
  {
    id: 'issuance',
    name: 'Issuance Module',
    tagline: 'Material requests, picklists, and issuance to NetSuite.',
    purpose:
      'The MRO floor workflow end-to-end: requesters submit MRS, planners assemble picklists, the mobile app handles physical picking, and MIS posts back to NetSuite once items leave the bin. Includes a dedicated dashboard for issuance KPIs.',
    status: 'live',
    deployedAt: '2026-03-11',
    icon: ArrowUpFromLine,
    highlights: [
      'Material Request (MRS) intake and review',
      'Multi-picker picklist assignment',
      'Mobile picking guide tied to picker identity',
      'MRO Dashboard — KPIs, aging, top requestors',
    ],
    submodules: [
      { code: 'MRSR', name: 'Material Requests', status: 'live', icon: FileText },
      { code: 'PICK', name: 'Picklists', status: 'live', icon: ClipboardList },
      { code: 'ISSE', name: 'Issuance Posting', status: 'live', icon: ArrowUpFromLine },
    ],
  },
  {
    id: 'receiving',
    name: 'Receiving Module',
    tagline: 'Bring purchased stock into the warehouse against an open PO.',
    purpose:
      'Pulls open purchase orders from NetSuite, lets the warehouse team capture received quantities (item-by-item, with bin and lot), and posts the receipt back. Built to handle partial deliveries and zero-quantity lines without breaking the workflow.',
    status: 'live',
    deployedAt: '2026-04-13',
    icon: ArrowDownToLine,
    highlights: [
      'Live PO list with received-quantity tracking',
      'Bin and lot capture during put-away',
      'Posts inventory receipt directly to NetSuite',
      'Resync detection for already-received quantities',
    ],
    submodules: [
      { code: 'RECV', name: 'Receiving / Put-Away', status: 'live', icon: ArrowDownToLine },
      { code: 'PORD', name: 'Purchase Orders', status: 'live', icon: ShoppingCart },
    ],
  },
  {
    id: 'quotations',
    name: 'Quotation Module',
    tagline: 'AI-assisted sales quotations and cutting-list submission to NetSuite.',
    purpose:
      'Lets the Sales team compose customer quotations, save them as DOP-persisted drafts, and submit a finalised cutting list to NetSuite. The standout for sales coordinators: paste a customer RFQ (text or file) and an AI extractor instantly turns it into structured rebar line items — no more manually keying in dozens of cuts, lengths, and grades.',
    status: 'live',
    deployedAt: '2026-04-27',
    icon: FileSpreadsheet,
    highlights: [
      'AI extraction — paste an RFQ document, get structured line items in seconds',
      'Replaces hours of typing for coordinators handling large or mixed-grade RFQs',
      'Cutting-list submit with header-level grade markups, sales-rep / coordinator wiring, and per-line bin allocation',
    ],
    submodules: [
      { code: 'AIEX', name: 'AI Line-Item Extractor', status: 'live', icon: Sparkles },
      { code: 'QUOT', name: 'Quotations', status: 'live', icon: FileSpreadsheet },
      { code: 'CUTL', name: 'Cutting List', status: 'live', icon: Scissors },
      { code: 'FRGT', name: 'Freight Computation', status: 'planned', icon: Truck },
      { code: 'QEML', name: 'Quotation Emailing Engine', status: 'planned', icon: Mail },
    ],
  },
];
